import { useMemo, useState, useEffect, type ReactElement } from 'react';
import { Outlet, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  Box, Skeleton, Chip, alpha, Typography, Alert, Stack, IconButton, Tooltip,
  useMediaQuery, useTheme,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import ServiceWorkspaceNav from '../../components/services/ServiceWorkspaceNav';
import { resolveServiceIcon } from '../../constants/serviceIcons';
import { HUB_META, resolveServiceDisplayName, type ServiceHubType } from '../../constants/serviceHubs';
import { resolveOnDemandChromeAccent } from '../../constants/onDemandTheme';
import { resolveServiceTabs, type FlowTab, type TabKey } from '../../constants/moduleFlows';
import { getHubService } from '../../services/serviceHubService';
import { getMasterCatalog } from '../../services/platform/masterService';
import { filterMasterCatalog } from '../../utils/serviceMasterCatalog';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { selectEnabledModules } from '../../store/slices/tenantSlice';
import { getRoleHomePath } from '../../config/roleNavigation';
import { selectUser } from '../../store/slices/authSlice';

const VALID_HUBS: ServiceHubType[] = ['transport', 'delivery', 'on_demand'];

function serviceTitle(hub: ServiceHubType, service: { name: string; slug?: string }) {
  return resolveServiceDisplayName(hub, service.slug, service.name) || service.name;
}

function platformModuleOf(service?: {
  settings?: Record<string, unknown>;
  serviceCode?: string;
  moduleKey?: string;
}) {
  if (!service) return undefined;
  const fromSettings = service.settings?.platformModule;
  if (typeof fromSettings === 'string') return fromSettings;
  return service.moduleKey || service.serviceCode;
}

function activeTabKey(
  pathname: string,
  hub: ServiceHubType,
  slug: string,
  tabs: FlowTab[],
): TabKey {
  const base = `/hubs/${hub}/${slug}`;
  const suffix = pathname.replace(base, '').replace(/^\//, '');
  if (!suffix) return (tabs.find((t) => t.key === 'masters')?.key || tabs[0]?.key || 'masters') as TabKey;
  const match = tabs.find((t) => t.key === suffix);
  return (match?.key || tabs[0]?.key || 'summary') as TabKey;
}

export default function ServiceDetailLayout() {
  const { hubType = 'transport', slug = '' } = useParams<{
    hubType: ServiceHubType;
    slug: string;
  }>();
  const hub = VALID_HUBS.includes(hubType as ServiceHubType) ? (hubType as ServiceHubType) : 'transport';
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  const { data: service, isLoading, error } = useQuery({
    queryKey: ['hub-service', hub, slug],
    queryFn: () => getHubService(hub, slug),
    enabled: !!slug && VALID_HUBS.includes(hub),
  });

  const platformModule = platformModuleOf(
    service as { settings?: Record<string, unknown>; serviceCode?: string; moduleKey?: string } | undefined,
  );
  const effectiveModule = hub === 'transport' || slug === 'transport'
    ? 'transportation'
    : platformModule;

  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const enabledModules = useSelector(selectEnabledModules);
  const user = useSelector(selectUser);

  const tabs = useMemo(
    () => resolveServiceTabs(hub, slug, platformModule),
    [hub, slug, platformModule],
  );

  const { data: catalogRaw = [] } = useQuery({
    queryKey: ['master-catalog', 'service', hub, slug],
    queryFn: () => getMasterCatalog({ scope: 'service', hub, slug }),
    enabled: !!slug && tabs.some((t) => t.key === 'masters'),
    staleTime: 5 * 60_000,
  });

  const masterCatalog = useMemo(
    () => filterMasterCatalog(catalogRaw, {
      embedded: true,
      serviceHub: hub,
      serviceSlug: slug,
      enabledModules,
      isSuperAdmin,
    }),
    [catalogRaw, hub, slug, enabledModules, isSuperAdmin],
  );

  const searchParams = new URLSearchParams(location.search);
  const activeMasterType = searchParams.get('type') || '';

  const defaultTab = tabs.find((t) => t.key === 'masters')?.key || tabs[0]?.key || 'masters';
  const accent = hub === 'on_demand'
    ? resolveOnDemandChromeAccent(service?.themeColor || HUB_META.on_demand.color)
    : (service?.themeColor || HUB_META[hub]?.color || '#2563eb');
  const base = `/hubs/${hub}/${slug}`;
  const title = service ? serviceTitle(hub, service) : '';
  const activeTab = activeTabKey(location.pathname, hub, slug, tabs);
  const activeTabMeta = tabs.find((t) => t.key === activeTab);
  const moduleDisabled = Boolean(
    !isLoading
    && service
    && !isSuperAdmin
    && effectiveModule
    && !enabledModules.includes(effectiveModule),
  );

  useEffect(() => {
    if (service?.hubType && service.hubType !== hub) {
      navigate(
        {
          pathname: `/hubs/${service.hubType}/${service.slug}/${activeTab || defaultTab}`,
          search: location.search,
        },
        { replace: true },
      );
    }
  }, [service, hub, navigate, activeTab, defaultTab, location.search]);

  useEffect(() => {
    const suffix = location.pathname.replace(base, '').replace(/^\//, '');
    if (!suffix) {
      navigate({ pathname: `${base}/${defaultTab}`, search: location.search }, { replace: true });
    }
  }, [location.pathname, location.search, base, defaultTab, navigate]);

  if (moduleDisabled) {
    return <Navigate to={getRoleHomePath(user?.userType)} replace />;
  }

  const openTab = (key: string) => {
    navigate({ pathname: `${base}/${key}` });
  };

  const openMaster = (groupKey: string, typeKey: string) => {
    navigate({ pathname: `${base}/masters`, search: `?group=${groupKey}&type=${typeKey}` });
  };

  return (
    <PageShell
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          Could not load service. It may have been removed or you may not have access.
        </Alert>
      )}

      {isLoading || !service ? (
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 2.5, flex: 1 }} />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Compact header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              mb: 2,
              p: 1.5,
              flexShrink: 0,
              borderRadius: 2.5,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              borderTop: `3px solid ${accent}`,
            }}
          >
            <Tooltip title={`Back to ${HUB_META[hub]?.label}`}>
              <IconButton
                type="button"
                aria-label={`Back to ${HUB_META[hub]?.label}`}
                onClick={() => navigate(`/hubs/${hub}`)}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.75,
                  color: 'text.secondary',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  '&:hover': {
                    color: accent,
                    borderColor: alpha(accent, 0.35),
                    bgcolor: alpha(accent, 0.06),
                  },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: accent,
                bgcolor: alpha(accent, 0.1),
                border: `1px solid ${alpha(accent, 0.18)}`,
                '& svg': { fontSize: 22 },
              }}
            >
              {resolveServiceIcon(service.icon) as ReactElement}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700} noWrap sx={{ fontSize: '1.1rem', letterSpacing: '-0.025em' }}>
                  {title}
                </Typography>
                <Chip
                  size="small"
                  label={service.isActive ? 'Active' : 'Off'}
                  color={service.isActive ? 'success' : 'default'}
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 700, fontSize: '0.65rem' }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {HUB_META[hub]?.label} · {activeTabMeta?.label || 'Workspace'}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: isLg ? '248px minmax(0, 1fr)' : '1fr',
              gridTemplateRows: isLg ? '1fr' : 'auto 1fr',
              gap: 2,
              alignItems: 'stretch',
              overflow: 'hidden',
            }}
          >
            {isLg ? (
              <Box
                sx={{
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  p: 1.25,
                  minHeight: 0,
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${alpha(theme.palette.text.primary, 0.22)} transparent`,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: alpha(theme.palette.text.primary, 0.18),
                    borderRadius: 6,
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    bgcolor: alpha(theme.palette.text.primary, 0.28),
                  },
                }}
              >
                <ServiceWorkspaceNav
                  tabs={tabs}
                  activeKey={activeTab}
                  onSelect={openTab}
                  masterCatalog={masterCatalog}
                  activeMasterType={activeMasterType}
                  onSelectMaster={openMaster}
                />
              </Box>
            ) : (
              <Box sx={{ gridColumn: '1 / -1', flexShrink: 0 }}>
                <ServiceWorkspaceNav
                  tabs={tabs}
                  activeKey={activeTab}
                  onSelect={openTab}
                  compact
                  masterCatalog={masterCatalog}
                  activeMasterType={activeMasterType}
                  onSelectMaster={openMaster}
                />
              </Box>
            )}

            <Box
              sx={{
                borderRadius: 2.5,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.75, md: 2.5 },
                  py: 1.75,
                  flexShrink: 0,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} letterSpacing="-0.02em">
                  {activeTabMeta?.label || 'Workspace'}
                </Typography>
                {activeTab !== 'masters' && (
                  <Typography variant="caption" color="text.secondary">
                    {title} · {HUB_META[hub]?.label}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1.5, md: 2.5 } }}>
                <Outlet
                  context={{
                    service: { ...service, name: title },
                    hub,
                    dateFrom,
                    dateTo,
                    setDateFrom,
                    setDateTo,
                    isLoading,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </PageShell>
  );
}

export interface ServiceDetailContext {
  service?: Awaited<ReturnType<typeof getHubService>>;
  hub: ServiceHubType;
  dateFrom: string;
  dateTo: string;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  isLoading: boolean;
}
